import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { API_CONFIG } from "../config/api.config";

const QRBookingVerification = () => {
  const { pnr } = useParams();
  const [searchParams] = useSearchParams();
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pnrFromQuery = searchParams.get("pnr");
  const effectivePnr = pnr || pnrFromQuery;
  const details = searchParams.get("details");

  const formatDateValue = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Convert time value (e.g., "13:00") to display format (e.g., "1pm to 2pm")
  const formatTimeDisplay = (timeValue) => {
    if (!timeValue) return "N/A";
    const hour = parseInt(timeValue.split(":")[0]);
    const currentHour = hour > 12 ? hour - 12 : hour;
    const currentPeriod = hour >= 12 ? "pm" : "am";
    const nextHour = hour + 1;
    const displayNextHour = nextHour > 12 ? nextHour - 12 : nextHour;
    const nextPeriod = nextHour >= 12 ? "pm" : "am";
    return `${currentHour}${currentPeriod} to ${displayNextHour}${nextPeriod}`;
  };

  const normalizeBookingData = (data) => {
    if (!data || !data.success) {
      return null;
    }

    if (data.booking) {
      const booking = data.booking;
      const bookingType = (data.bookingType || data.type || "ROOM").toString().toUpperCase();
      const type = bookingType.includes("RESTAURANT") ? "restaurant" : "room";

      return {
        type,
        pnr: data.pnr || booking.bookingPnr || booking.pnr || "N/A",
        customerName: booking.customerName || "Guest",
        paymentStatus: booking.paymentStatus || booking.status || "PENDING",
        totalAmount: booking.totalAmount || 0,
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        roomNumber: booking.room?.roomNumber || "N/A",
        bookingDate: booking.bookingDate,
        bookingTime: booking.bookingTime,
        specialRequests: booking.specialRequests,
        qrCodeUrl: booking.qrCodeUrl,
        pdfReceiptUrl: booking.pdfReceiptUrl,
        bookingId: booking.bookingId || booking.id,
      };
    }

    if (data.pnr || data.type) {
      return {
        ...data,
        bookingId: data.bookingId || data.id,
      };
    }

    return null;
  };

  const verifyBooking = useCallback(async (bookingPnr) => {
    setLoading(true);
    setError(null);

    try {
      const primaryResponse = await fetch(
        `${API_CONFIG.BASE_URL}/bookings/verify/${encodeURIComponent(bookingPnr)}`,
      );

      if (primaryResponse.ok) {
        const primaryData = await primaryResponse.json();
        const normalizedPrimary = normalizeBookingData(primaryData);
        if (normalizedPrimary) {
          setBookingData(normalizedPrimary);
          toast.success(`✅ ${normalizedPrimary.type} booking verified successfully!`);
          return;
        }
      }

      // Fallback endpoint for legacy/test data shape.
      const fallbackResponse = await fetch(
        `${API_CONFIG.BASE_URL}/test-qr/${encodeURIComponent(bookingPnr)}`,
      );

      if (!fallbackResponse.ok) {
        throw new Error("Booking not found");
      }

      const fallbackData = await fallbackResponse.json();
      const normalizedFallback = normalizeBookingData(fallbackData);
      if (!normalizedFallback) {
        throw new Error("Unable to read booking verification response");
      }

      setBookingData(normalizedFallback);
      toast.success(`✅ ${normalizedFallback.type} booking verified successfully!`);
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.message || "Failed to verify booking. Please try again.");
      toast.error("⚠️ Verification failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (effectivePnr) {
      verifyBooking(effectivePnr);
    } else {
      setLoading(false);
      setError("Booking reference not found in QR link.");
    }
  }, [effectivePnr, verifyBooking]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 sm:pt-28 px-4 bg-gray-50">
        <div className="min-h-[calc(100vh-7rem)] flex items-center justify-center">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-5 text-center">
            <div className="relative w-11 h-11 mx-auto mb-3">
            <div className="absolute inset-0 rounded-full border-4 border-red-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-red-500 border-t-transparent animate-spin"></div>
            </div>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">
              Verifying Your Booking
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Please wait while we validate booking {effectivePnr}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-24 sm:pt-28 px-4 bg-gray-50">
        <div className="min-h-[calc(100vh-7rem)] flex items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-5 text-center border border-red-100">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-7 h-7 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
              Verification Failed
            </h2>
            <p className="text-gray-500 mb-4 text-sm">{error}</p>
            <button
              onClick={() => verifyBooking(effectivePnr)}
              className="w-full bg-[#EE2E24] hover:bg-[#d9281f] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="min-h-screen pt-24 sm:pt-28 px-4 bg-gray-50">
        <div className="min-h-[calc(100vh-7rem)] flex items-center justify-center">
          <div className="text-center bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
            <p className="text-gray-600 font-semibold">No booking data available</p>
          </div>
        </div>
      </div>
    );
  }

  const isRestaurant = bookingData.type === "restaurant";
  const bookingStatus = (bookingData.paymentStatus || "").toUpperCase();
  const isSuccessfulStatus = ["SUCCESS", "COMPLETED", "CONFIRMED"].includes(bookingStatus);
  const baseUploadsUrl = API_CONFIG.BASE_URL.replace("/api", "");
  const qrDownloadUrl = bookingData.qrCodeUrl
    ? (bookingData.qrCodeUrl.startsWith("http")
      ? bookingData.qrCodeUrl
      : `${baseUploadsUrl}/uploads/${bookingData.qrCodeUrl}`)
    : null;
  const receiptDownloadUrl = bookingData.bookingId
    ? `${API_CONFIG.BASE_URL}/${isRestaurant ? "restaurant-bookings" : "room-bookings"}/${bookingData.bookingId}/download-receipt`
    : (bookingData.pdfReceiptUrl
      ? (bookingData.pdfReceiptUrl.startsWith("http")
        ? bookingData.pdfReceiptUrl
        : `${baseUploadsUrl}/uploads/${bookingData.pdfReceiptUrl}`)
      : null);

  const bookingTypeLabel = isRestaurant ? "Restaurant Booking" : "Room Booking";

  return (
    <div className="min-h-screen pt-24 sm:pt-28 pb-6 px-3 sm:px-4 bg-[radial-gradient(circle_at_top,_#fff7ed_0%,_#f9fafb_44%,_#f3f4f6_100%)]">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-[0_12px_36px_rgba(15,23,42,0.08)] border border-gray-200 overflow-hidden">
          <div className="relative bg-gradient-to-r from-[#EE2E24] to-[#f97316] px-4 sm:px-5 py-3.5 text-white">
            <div className="absolute right-0 top-0 h-full w-20 bg-white/10 blur-2xl"></div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/85">
                  Verification Complete
                </p>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight mt-0.5">
                  Booking Verified
                </h1>
                <p className="mt-1 text-white/95 text-[13px] font-semibold">
                  {details || `${bookingTypeLabel}: ${bookingData.pnr}`}
                </p>
              </div>
              <div className="bg-white/20 rounded-xl p-2 shrink-0 border border-white/30">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-3.5 sm:p-4 space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">
                  Booking PNR
                </p>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide ${
                    isSuccessfulStatus
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {bookingStatus || "PENDING"}
                </span>
              </div>
              <p className="text-[30px] leading-none font-black text-gray-900 tracking-tight">{bookingData.pnr}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2">
                  Customer
                </p>
                <p className="text-base sm:text-lg font-bold text-gray-900 tracking-tight leading-snug">
                  {bookingData.customerName || "Guest"}
                </p>
                <p className="text-xs font-semibold text-gray-500 mt-1">
                  {bookingTypeLabel}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2">
                  Amount
                </p>
                <p className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                  ₹{Number(bookingData.totalAmount || 0).toLocaleString("en-IN")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {isRestaurant ? (
                <>
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2">Booking Date</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{formatDateValue(bookingData.bookingDate)}</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2">Booking Time</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{formatTimeDisplay(bookingData.bookingTime)}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2">Check-In</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{formatDateValue(bookingData.checkIn)}</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2">Check-Out</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{formatDateValue(bookingData.checkOut)}</p>
                  </div>
                </>
              )}
            </div>

            {!isRestaurant && (
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-orange-600">Room Number</p>
                <p className="text-base sm:text-lg font-black text-orange-800">{bookingData.roomNumber || "N/A"}</p>
              </div>
            )}

            {isRestaurant && bookingData.specialRequests && (
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-orange-600 mb-1">Special Request</p>
                <p className="text-sm font-medium text-orange-900">{bookingData.specialRequests}</p>
              </div>
            )}

            {(qrDownloadUrl || receiptDownloadUrl) && (
              <div className="bg-white border border-gray-200 rounded-xl p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-2.5">Downloads</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {qrDownloadUrl && (
                    <a
                      href={qrDownloadUrl}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold py-2 px-3 rounded-lg transition-colors"
                    >
                      Download QR
                    </a>
                  )}
                  {receiptDownloadUrl && (
                    <a
                      href={receiptDownloadUrl}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold py-2 px-3 rounded-lg transition-colors"
                    >
                      Download Receipt
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="bg-gray-900 text-gray-200 rounded-xl p-3 border border-gray-800">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Support</p>
              <p className="mt-1 text-sm leading-relaxed">
                For any issue, contact support with PNR
                <span className="font-bold text-white"> {bookingData.pnr}</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRBookingVerification;
